import React from 'react';

import cn from 'classnames';

import { find, isEmpty, isEqual, noop } from 'lodash';
import { bool, func, shape, string, arrayOf } from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import { faCircleQuestion } from '@fortawesome/pro-regular-svg-icons/faCircleQuestion';
import { faFile } from '@fortawesome/pro-regular-svg-icons/faFile';
import { faWallet } from '@fortawesome/pro-regular-svg-icons/faWallet';
import { faArrowLeft } from '@fortawesome/pro-solid-svg-icons/faArrowLeft';
import { faEllipsisVertical } from '@fortawesome/pro-solid-svg-icons/faEllipsisVertical';

import IconButton from '@mui/material/IconButton';
import Popover from '@mui/material/Popover';

import * as googleTranslateActions from 'actions/googleTranslate';
import removeJwt from 'actions/removeJwt';

import Button from 'components/Common/Buttons/ButtonV3';
import ClickableComponent from 'components/Common/ClickableComponent';
import Flexbox from 'components/Common/Flexbox';
import FontAwesomeIcon from 'components/Common/FontAwesomeIcon';
import GoogleTranslate from 'components/Common/GoogleTranslateV2';
import CompanyName from 'components/Common/Icons/CompanyName';
import Avatar from 'components/Common/Layout/components/Avatar';
import HomeItem from 'components/Common/Layout/components/HomeItem';
import Icon from 'components/Common/Layout/components/Icon';
import Wrapper from 'components/Common/Layout/components/Wrapper';
import LanguageTranslationMenuItem from 'components/Common/LayoutV2/AppBar/components/LanguageTranslationMenuItem';
import Item from 'components/Common/LayoutV2/AppBar/DesktopV2/components/Item';
import Settings from 'components/Common/LayoutV2/AppBar/DesktopV2/components/Settings';
import LogoutButton from 'components/Common/LayoutV2/AppBar/LogoutButton';
import PageDetailsContainer from 'components/Common/LayoutV2/AppBar/PageDetails/PageDetailsContainer';
import TextToSpeech from 'components/Common/LayoutV2/AppBar/TextToSpeech';
import {
  getRelevantNavItems,
  isElementaryPage,
  getLogoLinkTo,
} from 'components/Common/LayoutV2/utils';
import ShadowLink from 'components/Common/ShadowLink';
import SupportLink from 'components/Common/SupportLink';
import Chat from 'components/CommonV2/Icons/Chat';
import LogoutIcon from 'components/CommonV2/Icons/Logout';

import { SUPPORT_URL } from 'config_app';
import { USER_TYPES } from 'constants/userTypes';

import withRouter from 'containers/withRouter';
import withUserType from 'containers/withUserType';

import withMediaInfo from 'HOC/withMediaInfo';
import withModal from 'HOC/withModal';

import ModalPropTypes, { ModalDefaultProps } from 'propTypes/ModalPropTypes';
import { RouterPropTypes } from 'propTypes/RouterPropTypes';
import {
  DOCUMENT_CENTER,
  LOGOUT,
  EL_STUDENT_DASHBOARD,
  EL_STUDENT_CAREER_QUIZ,
  LOGIN_K12,
  ONBOARDING,
  ROOT,
  CREDENTIAL_WALLET,
} from 'routes/constants';

import { ensureArray } from 'utils/arrays';

import getFullName from 'utils/getFullName';

import K12DashboardSwitcher from 'modules/AccountSetupV2/widgets/K12DashboardSwitcher';
import Notifications from 'modules/Notifications';
import Messages from 'modules/Notifications/Messages';
import StudentDemoAccount from 'modules/StudentDemoAccount';
import WelcomeWidget from 'modules/WelcomeWidget';

import s from './DesktopAppBarV2.css';

@connect(
  ({
    googleTranslate: { showGoogleTranslate },
    user: {
      profilePic,
      email,
      firstName,
      lastName,
      preferredName,
      moduleAccess,
      featureOnboarding,
      layout = {},
      activeUserType = [],
      currentUserMode,
    } = {},
    modals: { modalType } = {},
  }) => ({
    profilePic,
    email,
    firstName,
    lastName,
    preferredName,
    showGoogleTranslate,
    moduleAccess,
    featureOnboarding,
    appBar: getRelevantNavItems(
      layout,
      activeUserType,
      'appBar',
      currentUserMode,
    ),
    shouldRenderAffiliationLink:
      !activeUserType?.includes('member') &&
      activeUserType?.includes('guardian'),
    isModalVisible: Boolean(modalType),
  }),
  d => bindActionCreators(googleTranslateActions, d),
)
@withModal
@withMediaInfo()
@withUserType
@withRouter
class DesktopAppBarV2 extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      elementaryMobileMenuAnchor: null,
    };
  }

  shouldComponentUpdate(nextProps, nextStates) {
    return !isEqual(nextProps, this.props) || !isEqual(nextStates, this.state);
  }

  clearGuestOrganizationCache = () => {
    removeJwt();
    localStorage.removeItem('guestUserRedirectedEvent');
    localStorage.removeItem('guestUserFormValues');
  };

  renderWelcomeWidget = () => {
    const {
      user: { isCounselor },
      isOnboarding,
    } = this.props;

    if (isOnboarding) return null;

    return isCounselor ? (
      <WelcomeWidget key="internal-helper" />
    ) : (
      <SupportLink
        key="internal-helper"
        variant="hyperlink"
        text={
          <Icon key="internal-helper" aria-label="Go to support page">
            <FontAwesomeIcon size="xl" icon={faCircleQuestion} />
          </Icon>
        }
        url={SUPPORT_URL}
      />
    );
  };

  renderAvatar = (config = {}) => {
    const { withName, editProfile } = config;
    const { profilePic, firstName, lastName, isOnboarding, preferredName } =
      this.props;

    if (isOnboarding) return null;

    const fullName = getFullName({ firstName, lastName, preferredName });

    return (
      <Icon
        disableLinkWrapper
        className={cn(s.avatarIcon, withName && s.avatarWithName)}
        key="profile"
        aria-label={`Edit my profile ${fullName}`}
        linkTo={
          editProfile ? '/profile/edit' : '/account-settings/profile-photo'
        }
      >
        <Avatar
          tabIndex={-1}
          role="none"
          profilePic={profilePic}
          withName={withName}
          name={fullName}
          firstName={firstName}
          lastName={lastName}
        />
      </Icon>
    );
  };

  renderDemoStudentButton = item => (
    <StudentDemoAccount k12AdminTooltip={item?.tooltip} key="student-demo" />
  );

  renderDemoStudentText = () => {
    const {
      isOnboarding,
      user: { isDemoStudent },
    } = this.props;
    return (
      <div
        className={cn(s.studentDemoTitle, {
          [s.studentDemoTitlePadding]: isOnboarding && isDemoStudent,
        })}
      >
        Student Demo Mode
      </div>
    );
  };

  renderCredentialWalletButton = () => {
    const { isOnboarding } = this.props;
    if (isOnboarding) return null;

    return (
      <Icon
        disableLinkWrapper
        key="credential-wallet"
        linkTo={CREDENTIAL_WALLET}
        aria-label="Go to credential wallet"
      >
        <FontAwesomeIcon icon={faWallet} />
      </Icon>
    );
  };

  renderDocumentsButton = () => {
    const { isOnboarding } = this.props;
    if (isOnboarding) return null;

    return (
      <Icon
        disableLinkWrapper
        key="documentcenter"
        linkTo={DOCUMENT_CENTER}
        aria-label="Go to document center"
      >
        <FontAwesomeIcon icon={faFile} />
      </Icon>
    );
  };

  renderGoogleTranslate = ({ isElementary }) => {
    const {
      showGoogleTranslate,
      toggleGoogleTranslate,
      mediaInfo: { isDesktop },
    } = this.props;

    const getHidden = () => {
      if (isElementary) {
        return isDesktop ? false : !showGoogleTranslate;
      }
      return !showGoogleTranslate;
    };
    return (
      <GoogleTranslate
        key="google-translate"
        hidden={getHidden()}
        isElementary={isElementary}
        onClose={() => toggleGoogleTranslate(false)}
      />
    );
  };

  renderLogo = () => {
    const {
      isOnboarding,
      user: { isStudent, isDemoStudent, currentUserMode, isLoggedIn },
    } = this.props;

    const linkTo = getLogoLinkTo({
      isOnboarding,
      isStudent,
      isDemoStudent,
      currentUserMode,
      isLoggedIn,
    });

    return <HomeItem key="home" linkTo={linkTo} aria-label="Go to home page" />;
  };

  renderLogoWithName = () => {
    const {
      isOnboarding,
      user: { isStudent, isDemoStudent, type, currentUserMode, isLoggedIn },
      hideModal,
    } = this.props;

    const linkTo = getLogoLinkTo({
      isOnboarding,
      isStudent,
      isDemoStudent,
      currentUserMode,
      isLoggedIn,
    });

    const onHomeItemClick = () => {
      if (type === USER_TYPES.ORGANIZATION_GUEST) {
        this.clearGuestOrganizationCache();
      }
      hideModal();
    };

    return (
      <Flexbox key="home-with-logo" alignItems="center">
        <HomeItem href={linkTo} onClick={onHomeItemClick} />
        <CompanyName className={s.companyName} />
      </Flexbox>
    );
  };

  renderMemberButton = () => {
    const {
      appBarPageDetails,
      user: { type },
    } = this.props;

    const { memberButtonHref, loginRedirect, hideSignUp } = appBarPageDetails;

    // AccountSetupScreen checks query params for redirecting after creating account - can use memberButtonHref to pass params
    const href = memberButtonHref || ONBOARDING;
    const isOrgGuest = type === USER_TYPES.ORGANIZATION_GUEST;

    const onLoginClick = () => {
      if (isOrgGuest) {
        this.clearGuestOrganizationCache();
        window.location.href = ROOT;
      } else {
        window.location.href = LOGIN_K12;
        if (loginRedirect) {
          localStorage.setItem('loginRedirectURL', loginRedirect);
        }
      }
    };

    const onSignUpClick = () => {
      window.location.href = href;
      if (isOrgGuest) {
        this.clearGuestOrganizationCache();
      }
      if (loginRedirect) {
        localStorage.setItem('loginRedirectURL', loginRedirect);
      }
    };

    return type === 'guest' || isOrgGuest ? (
      <Flexbox key="member-button" gap={2} marginLeft="auto">
        <Button
          onClick={onLoginClick}
          className={s.memberButton}
          variant="outlined"
          label="Log in"
        />
        {!hideSignUp && (
          <Button
            onClick={onSignUpClick}
            className={s.signUpButton}
            variant="filled"
            label="Sign up"
          />
        )}
      </Flexbox>
    ) : null;
  };

  renderPageDetails = () => {
    const { appBarPageDetails } = this.props;
    const { title, modal, module, classes, hasFeatureOnboarding } =
      appBarPageDetails;

    return (
      <PageDetailsContainer
        key="page-details"
        title={title}
        module={module}
        modal={modal}
        classes={classes}
        hasFeatureOnboarding={hasFeatureOnboarding}
      />
    );
  };

  renderDashboardSwitcher = () => {
    const {
      router: { location },
    } = this.props;

    return (
      <K12DashboardSwitcher
        key="k12-dashboard-switcher"
        isCurrentlyElementaryPage={isElementaryPage(location.pathname)}
      />
    );
  };

  renderMessage = item => {
    const { isOnboarding } = this.props;
    if (isOnboarding) return null;

    return (
      <Icon key="messages" aria-label="View messages">
        <Messages disabled={!item.isEnabled} />
      </Icon>
    );
  };

  renderNotification = (item, onClick) => {
    const { isOnboarding } = this.props;
    if (isOnboarding) return null;

    return <Notifications onClick={onClick} />;
  };

  renderSettings = (item, onClick) => {
    const {
      showGoogleTranslate,
      toggleGoogleTranslate,
      moduleAccess,
      user,
      isOnboarding,
    } = this.props;

    return (
      <Settings
        showGoogleTranslate={showGoogleTranslate}
        toggleGoogleTranslate={toggleGoogleTranslate}
        moduleAccess={moduleAccess}
        isOnboarding={isOnboarding}
        user={user}
        onClick={onClick}
      />
    );
  };

  renderTextToSpeech = () => {
    const { color } = this.props;
    return <TextToSpeech key="test-to-speech" color={color} />;
  };

  renderLogoutButton = () => {
    const {
      mediaInfo,
      hideModal,
      showGoogleTranslate,
      toggleGoogleTranslate,
      router: { navigate },
    } = this.props;
    const { isDesktop } = mediaInfo;
    const { elementaryMobileMenuAnchor } = this.state;
    if (isDesktop) {
      return <LogoutButton key="logout-button" />;
    }

    return (
      <span key="logout-button">
        {this.renderGoogleTranslate({ isElementary: true })}
        <IconButton
          className={s.moreIcon}
          color="inherit"
          onClick={({ currentTarget }) =>
            this.setState({ elementaryMobileMenuAnchor: currentTarget })
          }
        >
          <FontAwesomeIcon icon={faEllipsisVertical} />
        </IconButton>
        <Popover
          anchorEl={elementaryMobileMenuAnchor}
          open={Boolean(elementaryMobileMenuAnchor)}
          onClose={() => this.setState({ elementaryMobileMenuAnchor: null })}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
        >
          <LanguageTranslationMenuItem
            showIcon
            className={s.menuItem}
            showGoogleTranslate={showGoogleTranslate}
            onClickCallback={() => {
              toggleGoogleTranslate(!showGoogleTranslate);
              this.setState({ elementaryMobileMenuAnchor: null });
            }}
          />
          <ClickableComponent className={cn('intercom-launcher', s.menuItem)}>
            <Chat />
            Chat Support
          </ClickableComponent>
          <ClickableComponent
            className={s.menuItem}
            onClick={() => {
              hideModal();
              navigate(LOGOUT);
            }}
          >
            <LogoutIcon />
            Logout
          </ClickableComponent>
        </Popover>
      </span>
    );
  };

  renderBackButton = () => {
    const handleBackButtonClick = event => {
      const {
        router: { navigate, location },
        hideModal,
        isModalVisible,
      } = this.props;
      event.preventDefault();

      if (isModalVisible) {
        return hideModal();
      }

      if (location.pathname === EL_STUDENT_CAREER_QUIZ) {
        return navigate(EL_STUDENT_DASHBOARD);
      }
      return navigate(-1);
    };

    return (
      <ShadowLink
        key="back-button"
        icon={<FontAwesomeIcon icon={faArrowLeft} />}
        tooltipTitle="Go back"
        linkClass={s.backBtn}
        onClick={handleBackButtonClick}
      />
    );
  };

  renderCompanyName = () => (
    <CompanyName key="company-name" className={s.companyName} />
  );

  render() {
    const {
      featureOnboarding,
      router: { location },
      appBar,
      showBackButton,
      appBarPageDetails,
      ...restProps
    } = this.props;
    const { left: leftKeys = [], right: rightKeys = [] } = { ...appBar };

    const feature = find(featureOnboarding, o =>
      location.pathname.includes(o.feUrl),
    );

    const getAppBarItem = item => {
      const renderFunction = {
        'app-bar-avatar': this.renderAvatar,
        'app-bar-credential-wallet': this.renderCredentialWalletButton,
        'app-bar-demo-student-button': this.renderDemoStudentButton,
        'app-bar-demo-student-text': this.renderDemoStudentText,
        'app-bar-documents': this.renderDocumentsButton,
        'app-bar-edit-profile-avatar': thisItem =>
          this.renderAvatar({ withName: true, editProfile: true }, thisItem),
        'app-bar-google-translate': this.renderGoogleTranslate,
        'app-bar-google-translate-elementary': () =>
          this.renderGoogleTranslate({ isElementary: true }),
        'app-bar-home': this.renderLogo,
        'app-bar-home-name': this.renderLogoWithName,
        'app-bar-member': this.renderMemberButton,
        'app-bar-page-details': this.renderPageDetails,
        'app-bar-k12-admin-dashboard-switcher': this.renderDashboardSwitcher,
        'app-bar-message': this.renderMessage,
        'app-bar-notification': this.renderNotification,
        'app-bar-settings': this.renderSettings,
        'app-bar-welcome-widget': this.renderWelcomeWidget,
        'app-bar-schoolinks': !showBackButton ? this.renderCompanyName : null,
        'app-bar-speaker': this.renderTextToSpeech,
        'app-bar-logout': this.renderLogoutButton,
        'app-bar-back-button': showBackButton ? this.renderBackButton : null,
      }[item.key];

      const isManagedTooltip = [
        'app-bar-demo-student-button',
        'app-bar-notification',
        'app-bar-settings',
      ].includes(item.key);

      if (typeof renderFunction === 'function') {
        return item?.tooltip && !isManagedTooltip ? (
          <Item item={item} renderFunction={renderFunction} />
        ) : (
          renderFunction(item)
        );
      }
      return null;
    };

    const conditionalRightKeys =
      feature && !rightKeys.map(e => e.key).includes('app-bar-welcome-widget')
        ? ['app-bar-welcome-widget', ...rightKeys]
        : rightKeys;

    const rightKeysArray = Array.isArray(conditionalRightKeys)
      ? conditionalRightKeys
      : [conditionalRightKeys];

    window.rightKeysArray = rightKeysArray;

    const [firstItem, ...restItems] = leftKeys;
    const publicMemberPage = appBarPageDetails?.isPublicMemberPage
      ? [{ key: 'app-bar-home-name' }, { key: 'app-bar-member' }]
      : null;
    const tempLeftKeys = [
      firstItem,
      !isEmpty(appBarPageDetails) && !appBarPageDetails?.isPublicMemberPage
        ? { key: 'app-bar-page-details' }
        : null,
      ...ensureArray(publicMemberPage),
      ...restItems,
    ].filter(item => item);

    return (
      <>
        <Wrapper
          {...restProps}
          // eslint-disable-next-line no-inline-styles/no-inline-styles
          style={{ paddingRight: 24 }}
          leftIcons={[
            ...tempLeftKeys,
            { key: 'app-bar-back-button' },
            { key: 'app-bar-k12-admin-dashboard-switcher' },
          ].map(item => getAppBarItem(item))}
          rightIcons={[...rightKeysArray].map(getAppBarItem)}
          isPublicMemberPage={appBarPageDetails?.isPublicMemberPage}
        />
      </>
    );
  }
}

DesktopAppBarV2.propTypes = {
  ...ModalPropTypes,
  user: shape({}),
  isOnboarding: bool,
  profilePic: string,
  firstName: string,
  lastName: string,
  preferredName: string,
  showGoogleTranslate: bool,
  appBarPageDetails: shape({
    isPublicMemberPage: bool,
    memberButtonHref: string,
    loginRedirect: shape({}),
    hideSignUp: bool,
  }),
  toggleGoogleTranslate: func,
  moduleAccess: shape({}),
  mediaInfo: shape({}).isRequired,
  router: RouterPropTypes,
  isModalVisible: bool,
  shouldRenderAffiliationLink: bool,
  featureOnboarding: arrayOf(shape({})),
  appBar: shape({}),
  showBackButton: bool,
};

DesktopAppBarV2.defaultProps = {
  ...ModalDefaultProps,
  isOnboarding: false,
  preferredName: null,
  /**
   * @param isPublicMemberPage - If true, show app-bar-member
   * @param memberButtonHref - Url for onboarding when user presses sign up
   * @param loginRedirect - Redirects users after logging in (LoginRedirect). Needs to be in correct object shape for hooks/useRedirect
   * @param hideSignUp - If true, hides sign up button
   */
  appBarPageDetails: {
    isPublicMemberPage: false,
    memberButtonHref: null,
    loginRedirect: null,
    hideSignUp: false,
  },
  router: null,
  user: {},
  profilePic: null,
  firstName: null,
  lastName: null,
  showGoogleTranslate: false,
  toggleGoogleTranslate: noop,
  moduleAccess: null,
  isModalVisible: false,
  shouldRenderAffiliationLink: false,
  featureOnboarding: [],
  appBar: null,
  showBackButton: false,
};

export default DesktopAppBarV2;
